import React, { useState, useEffect } from "react";
import axios from "axios";
import Toast from "../StudentDashBoard/Toast";
import {
  Wrench,
  Camera,
  CheckCircle,
  Clock,
  XCircle,
  ChevronRight,
  Calendar,
  Home,
  Image,
  X,
  Plus,
  FileText,
  Send,
  Loader,
  Eye,
  MessageCircle,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Flame,
  Edit,
  Trash2,
  Zap,
  Droplets,
  Thermometer,
  Bug,
  Brush,
  Shield,
  AlertCircle
} from "lucide-react";

const Maintenance = () => {
  const [toast, setToast] = useState({ message: "", type: "" });
  const [canSubmit, setCanSubmit] = useState(false);
  const [roomInfo, setRoomInfo] = useState(null);
  const [requests, setRequests] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ Pending: 0, InProgress: 0, Completed: 0, Total: 0 });
  const [showForm, setShowForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    category: "",
    description: "",
    priority: "Medium"
  });
  const [attachments, setAttachments] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  const categories = [
    { value: "Plumbing", label: "Plumbing", icon: Droplets, color: "bg-cyan-100 text-cyan-700" },
    { value: "Electrical", label: "Electrical", icon: Zap, color: "bg-yellow-100 text-yellow-700" },
    { value: "Furniture", label: "Furniture", icon: Home, color: "bg-orange-100 text-orange-700" },
    { value: "Appliance", label: "Appliance", icon: Wrench, color: "bg-gray-100 text-gray-700" },
    { value: "Heating/Cooling", label: "Heating/Cooling", icon: Thermometer, color: "bg-red-100 text-red-700" },
    { value: "Pest Control", label: "Pest Control", icon: Bug, color: "bg-green-100 text-green-700" },
    { value: "Cleaning", label: "Cleaning", icon: Brush, color: "bg-blue-100 text-blue-700" },
    { value: "Security", label: "Security", icon: Shield, color: "bg-purple-100 text-purple-700" },
    { value: "Other", label: "Other", icon: AlertCircle, color: "bg-gray-100 text-gray-700" }
  ];

  const priorities = [
    { 
      value: "Low", 
      label: "Low", 
      color: "bg-green-100 text-green-800", 
      icon: TrendingDown,
      iconColor: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200"
    },
    { 
      value: "Medium", 
      label: "Medium", 
      color: "bg-yellow-100 text-yellow-800", 
      icon: AlertTriangle,
      iconColor: "text-yellow-600",
      bgColor: "bg-yellow-50",
      borderColor: "border-yellow-200"
    },
    { 
      value: "High", 
      label: "High", 
      color: "bg-orange-100 text-orange-800", 
      icon: TrendingUp,
      iconColor: "text-orange-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200"
    },
    { 
      value: "Emergency", 
      label: "Emergency", 
      color: "bg-red-100 text-red-800", 
      icon: Flame,
      iconColor: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200"
    }
  ];

  const statusConfig = {
    "Pending": { color: "bg-yellow-100 text-yellow-800", icon: Clock, label: "Pending Review", bgColor: "bg-yellow-50", borderColor: "border-yellow-200" },
    "In Progress": { color: "bg-blue-100 text-blue-800", icon: Loader, label: "In Progress", bgColor: "bg-blue-50", borderColor: "border-blue-200" },
    "Completed": { color: "bg-green-100 text-green-800", icon: CheckCircle, label: "Completed", bgColor: "bg-green-50", borderColor: "border-green-200" },
    "Cancelled": { color: "bg-gray-200 text-gray-800", icon: XCircle, label: "Cancelled", bgColor: "bg-gray-50", borderColor: "border-gray-200" }
  };

  const studentId = sessionStorage.getItem("StudentUserId");

  // Fetch maintenance requests
  const fetchRequests = async () => {
    try {
      const response = await axios.get(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Maintenance/student/${studentId}`);
      setRequests(response.data);
    } catch (err) {
      console.error("Error fetching requests:", err);
      setToast({ message: "Failed to load maintenance requests", type: "error" });
    }
  };

  // Fetch status counts
  const fetchStatusCounts = async () => {
    try {
      const response = await axios.get(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Maintenance/status-counts/${studentId}`);
      setStatusCounts({
        Pending: response.data.pending || 0,
        InProgress: response.data.inProgress || 0,
        Completed: response.data.completed || 0,
        Total: response.data.total || 0
      });
    } catch (err) {
      console.error("Error fetching counts:", err);
    }
  };

  // Delete request
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this maintenance request? This action cannot be undone.")) {
      try {
        await axios.delete(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net//api/Maintenance/${id}`);
        setToast({ message: "Maintenance request deleted successfully!", type: "success" });
        fetchRequests();
        fetchStatusCounts();
      } catch (err) {
        console.error("Error deleting:", err);
        setToast({ message: "Failed to delete request", type: "error" });
      }
    }
  };

  // Edit request
  const handleEdit = (request) => {
    setEditingId(request.id);
    setFormData({
      category: request.category,
      description: request.description,
      priority: request.priority
    });
    setShowEditForm(true);
  };

  // Update request
  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      setToast({ message: "Please select a category", type: "error" });
      return;
    }
    
    if (!formData.description.trim()) {
      setToast({ message: "Please describe the issue", type: "error" });
      return;
    }

    setIsSubmitting(true);

    try {
      const updateData = {
        category: formData.category,
        description: formData.description,
        priority: formData.priority
      };
      
      await axios.put(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Maintenance/${editingId}`, updateData);
      
      setToast({ message: "Maintenance request updated successfully!", type: "success" });
      setShowEditForm(false);
      setEditingId(null);
      resetForm();
      fetchRequests();
      fetchStatusCounts();
    } catch (err) {
      console.error("Error updating:", err);
      setToast({ message: err.response?.data?.message || "Failed to update request", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if request can be edited/deleted (only Pending status)
  const canModify = (status) => {
    return status === "Pending";
  };

  // Check if student can submit requests
  useEffect(() => {
    const checkEligibility = async () => {
      try {
        const response = await axios.get(`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Maintenance/can-submit/${studentId}`);
        setCanSubmit(response.data.canSubmit);
        if (response.data.roomNumber) {
          setRoomInfo(response.data);
        }
      } catch (err) {
        console.error("Error checking eligibility:", err);
      }
    };

    if (studentId) {
      checkEligibility();
    }
  }, [studentId]);

  useEffect(() => {
    if (studentId && canSubmit) {
      fetchRequests();
      fetchStatusCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, canSubmit]);

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = [...attachments, ...files];
    setAttachments(newAttachments);

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrls(prev => [...prev, { url: reader.result, name: file.name, size: file.size }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      setToast({ message: "Please select a category", type: "error" });
      return;
    }
    
    if (!formData.description.trim()) {
      setToast({ message: "Please describe the issue", type: "error" });
      return;
    }

    setIsSubmitting(true);

    const submitFormData = new FormData();
    submitFormData.append("StudentId", studentId);
    submitFormData.append("Category", formData.category);
    submitFormData.append("Description", formData.description);
    submitFormData.append("Priority", formData.priority);
    
    attachments.forEach(file => {
      submitFormData.append("Attachments", file);
    });

    try {
      await axios.post("https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Maintenance", submitFormData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setToast({ message: "Maintenance request submitted successfully!", type: "success" });
      setShowForm(false);
      resetForm();
      fetchRequests();
      fetchStatusCounts();
    } catch (err) {
      console.error("Error submitting:", err);
      setToast({ message: err.response?.data?.message || "Failed to submit request", type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ category: "", description: "", priority: "Medium" });
    setAttachments([]);
    setPreviewUrls([]);
    setEditingId(null);
  };

  const viewRequestDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailModal(true);
  };

  const getPriorityInfo = (priority) => {
    const found = priorities.find(p => p.value === priority) || priorities[1];
    const IconComponent = found.icon;
    return {
      ...found,
      iconComponent: IconComponent
    };
  };

  const getStatusInfo = (status) => {
    return statusConfig[status] || statusConfig["Pending"];
  };

  const getCategoryInfo = (category) => {
    return categories.find(c => c.value === category) || categories[8];
  };

  // If student has no room assigned
  if (!canSubmit) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Home className="w-10 h-10 text-indigo-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No room assigned</h2>
          <p className="text-gray-600 mb-6">
            You need to be assigned to a room before you can submit maintenance requests.
            Please contact residence management for assistance.
          </p>
          <button
            onClick={() => window.location.href = "/dashboard"}
            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition shadow-sm"
          >
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-700 p-6 md:p-8 text-white shadow-lg">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/15 backdrop-blur rounded-2xl shadow-inner">
              <Wrench className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Maintenance requests</h1>
              <p className="text-blue-100 text-sm mt-1">Report issues with your room and track their resolution status</p>
              {roomInfo && (
                <div className="mt-2 flex items-center gap-2 text-sm text-blue-100">
                  <Home className="w-4 h-4" />
                  Room {roomInfo.roomNumber}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="px-5 py-2.5 bg-white text-indigo-700 rounded-xl font-semibold hover:bg-indigo-50 transition shadow-md flex items-center gap-2 flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            New request
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 shadow-sm border border-amber-100 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-700 font-medium">Pending</p>
              <p className="text-3xl font-bold text-amber-800">{statusCounts.Pending}</p>
            </div>
            <div className="p-3 bg-white rounded-xl shadow-sm">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 shadow-sm border border-blue-100 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700 font-medium">In progress</p>
              <p className="text-3xl font-bold text-blue-800">{statusCounts.InProgress}</p>
            </div>
            <div className="p-3 bg-white rounded-xl shadow-sm">
              <Loader className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 shadow-sm border border-emerald-100 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-700 font-medium">Completed</p>
              <p className="text-3xl font-bold text-emerald-800">{statusCounts.Completed}</p>
            </div>
            <div className="p-3 bg-white rounded-xl shadow-sm">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-violet-50 to-violet-100 rounded-xl p-4 shadow-sm border border-violet-100 hover:shadow-md transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-violet-700 font-medium">Total tickets</p>
              <p className="text-3xl font-bold text-violet-800">{statusCounts.Total}</p>
            </div>
            <div className="p-3 bg-white rounded-xl shadow-sm">
              <FileText className="w-5 h-5 text-violet-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Requests List */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Your requests</h3>
            <p className="text-sm text-gray-500 mt-0.5">View progress and details of everything you've submitted</p>
          </div>
          {requests.length > 0 && (
            <span className="text-xs font-medium text-indigo-700 bg-indigo-50 px-3 py-1.5 rounded-full border border-indigo-100">
              {requests.length} request{requests.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {requests.length === 0 ? (
            <div className="col-span-1 md:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="w-20 h-20 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Wrench className="w-10 h-10 text-indigo-300" />
              </div>
              <p className="text-gray-500 text-lg">No maintenance requests yet</p>
              <p className="text-sm text-gray-400 mt-1">Click "New request" to report an issue</p>
            </div>
          ) : (
            requests.map((request) => {
              const priorityInfo = getPriorityInfo(request.priority);
              const statusInfo = getStatusInfo(request.status);
              const categoryInfo = getCategoryInfo(request.category);
              const StatusIcon = statusInfo.icon;
              const PriorityIcon = priorityInfo.iconComponent;
              const CategoryIcon = categoryInfo.icon;
              const canModifyRequest = canModify(request.status);
              
              return (
                <div
                  key={request.id}
                  className={`bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-200 border-l-4 ${priorityInfo.borderColor} group`}
                >
                  <div className="p-5">
                    {/* Header with Status and Actions */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${statusInfo.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusInfo.label}
                        </span>
                        <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${priorityInfo.color}`}>
                          <PriorityIcon className={`w-3 h-3 ${priorityInfo.iconColor}`} />
                          {priorityInfo.label} priority
                        </span>
                      </div>
                      
                      {/* Action Buttons - Only show for Pending requests */}
                      {canModifyRequest && (
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEdit(request); }}
                            className="p-1.5 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Edit request"
                          >
                            <Edit className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(request.id); }}
                            className="p-1.5 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete request"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Category and Title */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`p-2 rounded-xl ${categoryInfo.color}`}>
                        <CategoryIcon className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-medium text-gray-600">{request.category}</span>
                    </div>
                    
                    {/* Description */}
                    <p className="text-gray-700 line-clamp-2 mb-3 text-sm">
                      {request.description}
                    </p>
                    
                    {/* Footer with Date and Attachments */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(request.createdAt).toLocaleDateString()}
                        </span>
                        {request.attachments?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Image className="w-3 h-3" />
                            {request.attachments.length}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => viewRequestDetails(request)}
                        className="text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center gap-1 transition-colors"
                      >
                        View details
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Create Request Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-white" />
                <h3 className="text-lg font-bold text-white">New maintenance request</h3>
              </div>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 mb-5">
                <p className="text-sm text-indigo-800 font-medium">Room information</p>
                <p className="text-indigo-700">Room {roomInfo?.roomNumber}</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 bg-white text-gray-800"
                  required
                >
                  <option value="" className="text-gray-500">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value} className="text-gray-800">{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Priority level</label>
                <div className="grid grid-cols-2 gap-2">
                  {priorities.map(p => {
                    const IconComponent = p.icon;
                    return (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, priority: p.value })}
                        className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                          formData.priority === p.value
                            ? `${p.color} ring-2 ring-offset-2 ring-indigo-500 font-semibold`
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <IconComponent className={`w-4 h-4 ${p.iconColor}`} />
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 bg-white text-gray-800 placeholder-gray-400"
                  placeholder="Please describe the issue in detail..."
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Photos (optional)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-indigo-400 transition bg-gray-50">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">Click to upload photos</p>
                    <p className="text-xs text-gray-500">PNG, JPG up to 5MB</p>
                  </label>
                </div>

                {previewUrls.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    {previewUrls.map((preview, idx) => (
                      <div key={idx} className="relative group">
                        <img src={preview.url} alt="Preview" className="w-full h-24 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => removeAttachment(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition flex items-center justify-center gap-2 font-semibold shadow-sm disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {isSubmitting ? "Submitting..." : "Submit request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Request Modal */}
      {showEditForm && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-white" />
                <h3 className="text-lg font-bold text-white">Edit maintenance request</h3>
              </div>
              <button onClick={() => { setShowEditForm(false); resetForm(); }} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleUpdate} className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 bg-white text-gray-800"
                  required
                >
                  <option value="" className="text-gray-500">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value} className="text-gray-800">{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Priority level</label>
                <div className="grid grid-cols-2 gap-2">
                  {priorities.map(p => {
                    const IconComponent = p.icon;
                    return (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, priority: p.value })}
                        className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
                          formData.priority === p.value
                            ? `${p.color} ring-2 ring-offset-2 ring-indigo-500 font-semibold`
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <IconComponent className={`w-4 h-4 ${p.iconColor}`} />
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 bg-white text-gray-800 placeholder-gray-400"
                  placeholder="Please describe the issue in detail..."
                  required
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowEditForm(false); resetForm(); }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition flex items-center justify-center gap-2 font-semibold shadow-sm disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {isSubmitting ? "Updating..." : "Update request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Request Detail Modal */}
      {showDetailModal && selectedRequest && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-white" />
                <h3 className="text-lg font-bold text-white">Request details</h3>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)] bg-gray-50">
              <div className="flex gap-2 mb-4 flex-wrap">
                {(() => {
                  const priorityInfo = getPriorityInfo(selectedRequest.priority);
                  const PriorityIcon = priorityInfo.iconComponent;
                  return (
                    <span className={`inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full ${priorityInfo.color}`}>
                      <PriorityIcon className={`w-4 h-4 ${priorityInfo.iconColor}`} />
                      {selectedRequest.priority} priority
                    </span>
                  );
                })()}
                {(() => {
                  const statusInfo = getStatusInfo(selectedRequest.status);
                  return (
                    <span className={`inline-flex items-center gap-1.5 text-sm px-3 py-1 rounded-full ${statusInfo.color}`}>
                      {selectedRequest.status}
                    </span>
                  );
                })()}
              </div>

              <div className="mb-4 bg-white rounded-xl p-4 border border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Category</p>
                <p className="font-medium text-gray-800">{selectedRequest.category}</p>
              </div>

              <div className="mb-4 bg-white rounded-xl p-4 border border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Description</p>
                <p className="text-gray-800 whitespace-pre-wrap">{selectedRequest.description}</p>
              </div>

              <div className="mb-4 bg-indigo-50 border border-indigo-100 rounded-xl p-4">
                <p className="text-xs text-indigo-600 mb-1">Location</p>
                <p className="text-indigo-800 font-medium">Room {selectedRequest.roomNumber} &middot; {selectedRequest.residenceName}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Submitted</p>
                  <p className="text-sm text-gray-800">{new Date(selectedRequest.createdAt).toLocaleString()}</p>
                </div>
                {selectedRequest.resolvedAt && (
                  <div className="bg-white rounded-xl p-4 border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Resolved</p>
                    <p className="text-sm text-gray-800">{new Date(selectedRequest.resolvedAt).toLocaleString()}</p>
                  </div>
                )}
              </div>

              {selectedRequest.adminNotes && (
                <div className="mb-4 bg-blue-50 border border-blue-100 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="w-4 h-4 text-blue-600" />
                    <p className="text-sm font-semibold text-blue-800">Staff response</p>
                  </div>
                  <p className="text-sm text-blue-700">{selectedRequest.adminNotes}</p>
                </div>
              )}

              {selectedRequest.attachments && selectedRequest.attachments.length > 0 && (
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <p className="text-sm font-semibold text-gray-700 mb-3">Attached photos</p>
                  <div className="grid grid-cols-3 gap-3">
                    {selectedRequest.attachments.map((att) => (
                      <a
                        key={att.id}
                        href={`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/${att.filePath}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative group"
                      >
                        <img
                          src={`https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/${att.filePath}`}
                          alt="Attachment"
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                          onError={(e) => {
                            e.target.src = "https://via.placeholder.com/100?text=Image+Not+Found";
                          }}
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center rounded-lg">
                          <Eye className="w-5 h-5 text-white" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast.message && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: "", type: "" })} />
      )}
    </div>
  );
};

export default Maintenance;